import React from "react";
import { Link } from "react-router-dom";
import { useT, useLang } from "../context/LanguageContext";
import { t } from "../data/translations";
import { TID } from "../constants/testIds";

const HERO_IMG =
    "https://images.pexels.com/photos/5163935/pexels-photo-5163935.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400";

const Stat = ({ value, label, testid }) => (
    <div
        data-testid={testid}
        className="bg-white rounded-xl border border-slate-200 p-5 sm:p-6 text-center shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
    >
        <div className="text-3xl sm:text-4xl font-extrabold bg-gradient-to-br from-red-700 to-amber-600 bg-clip-text text-transparent">
            {value}
        </div>
        <div className="mt-1.5 text-xs sm:text-sm font-semibold text-slate-600 uppercase tracking-wider">
            {label}
        </div>
    </div>
);

const ServiceCard = ({ icon, title, desc, to, testid, accent }) => (
    <Link
        to={to}
        data-testid={testid}
        className={`group block bg-white rounded-xl border border-slate-200 p-6 hover:shadow-lg hover:-translate-y-1 hover:border-amber-500/50 transition-all`}
    >
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-4 ${accent}`}>
            <i className={`fa-solid ${icon} text-xl`} />
        </div>
        <h3 className="text-lg font-bold text-slate-900 mb-1.5">{title}</h3>
        <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
        <div className="mt-4 text-red-700 font-semibold text-sm inline-flex items-center gap-1.5 group-hover:gap-2.5 transition-all">
            <span>→</span>
            <span>Explore</span>
        </div>
    </Link>
);

const Home = () => {
    const tr = useT();
    const { lang } = useLang();

    return (
        <div>
            {/* HERO */}
            <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-red-900 to-amber-900 text-white">
                <div
                    className="absolute inset-0 opacity-25"
                    style={{ backgroundImage: `url(${HERO_IMG})`, backgroundSize: "cover", backgroundPosition: "center" }}
                />
                <div className="absolute inset-0 hero-grain" />
                <div className="absolute inset-0 bg-gradient-to-r from-slate-900/85 via-slate-900/60 to-transparent" />

                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
                    <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-2 bg-amber-500/20 border border-amber-300/30 text-amber-100 px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-widest mb-6 backdrop-blur-sm">
                            <i className="fa-solid fa-award text-amber-300" />
                            {tr(t.org.regNo)}
                        </div>

                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.15] mb-6">
                            {lang === "mr" ? (
                                <>
                                    तुमच्या स्वप्नांना <span className="text-amber-300">आर्थिक बळ</span> देणारी
                                    <br />
                                    विश्वासाची पतपेढी
                                </>
                            ) : (
                                <>
                                    Your Trusted Partner for
                                    <br />
                                    <span className="text-amber-300">Financial Growth</span> & Security
                                </>
                            )}
                        </h1>

                        <p className="text-base sm:text-lg text-slate-200 max-w-2xl mb-8 leading-relaxed">
                            {lang === "mr"
                                ? "१९९६ पासून ठाण्यातील सर्वसामान्य नागरिक, व्यावसायिक, महिला आणि ज्येष्ठ नागरिकांसाठी सुरक्षित ठेवी आणि जलद कर्ज सेवा."
                                : "Since 1996, serving Thane's citizens, businesses, women and seniors with safe deposits, attractive returns, and fast loan processing."}
                        </p>

                        <div className="flex flex-wrap items-center gap-3">
                            <Link
                                to="/contact"
                                data-testid={TID.heroCtaContact}
                                className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold px-6 py-3.5 rounded-lg transition-colors shadow-lg"
                            >
                                {tr(t.cta.contactUs)} <i className="fa-solid fa-arrow-right" />
                            </Link>
                            <Link
                                to="/membership"
                                data-testid={TID.heroCtaMember}
                                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/30 text-white font-semibold px-6 py-3.5 rounded-lg transition-colors"
                            >
                                {tr(t.cta.joinMember)}
                            </Link>
                        </div>

                        {/* Office hours strip */}
                        <div className="mt-10 inline-flex flex-wrap items-center gap-x-6 gap-y-2 bg-white/10 backdrop-blur-sm border border-white/20 px-5 py-3 rounded-lg text-sm">
                            <span className="font-semibold text-amber-200 uppercase tracking-wider text-xs">
                                {tr(t.hours.title)}
                            </span>
                            <span>{tr(t.hours.weekdays)}</span>
                            <span className="text-slate-300">·</span>
                            <span>{tr(t.hours.sunday)}</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* STATS */}
            <section className="bg-slate-50 border-b border-slate-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
                        <Stat value="29+" label={tr(t.home.statYears)} testid={TID.statCard("years")} />
                        <Stat value="10,000+" label={tr(t.home.statMembers)} testid={TID.statCard("members")} />
                        <Stat value="7" label={tr(t.home.statSchemes)} testid={TID.statCard("schemes")} />
                        <Stat value="12+" label={tr(t.home.statDeposit)} testid={TID.statCard("deposit")} />
                    </div>
                </div>
            </section>

            {/* SERVICES */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
                <div className="max-w-3xl mb-12">
                    <div className="text-xs uppercase tracking-widest text-amber-600 font-bold mb-2">
                        {lang === "mr" ? "आमच्या योजना" : "What We Offer"}
                    </div>
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-3">
                        {tr(t.home.servicesTitle)}
                    </h2>
                    <p className="text-slate-600 text-base sm:text-lg">{tr(t.home.servicesSub)}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                    <ServiceCard
                        icon="fa-vault"
                        title={tr(t.deposits.tabs.fd)}
                        desc={lang === "mr" ? "९% ते १०% पर्यंत आकर्षक व्याजदर." : "Attractive 9% – 10% PA returns on FDs."}
                        to="/deposits"
                        testid={TID.serviceCard("fd")}
                        accent="bg-red-50 text-red-700"
                    />
                    <ServiceCard
                        icon="fa-arrow-trend-up"
                        title={tr(t.deposits.tabs.rd)}
                        desc={lang === "mr" ? "मासिक बचतीतून मोठी रक्कम तयार करा." : "Systematic monthly savings up to 11.5% PA."}
                        to="/deposits"
                        testid={TID.serviceCard("rd")}
                        accent="bg-amber-50 text-amber-700"
                    />
                    <ServiceCard
                        icon="fa-money-bill-trend-up"
                        title={tr(t.deposits.tabs.mip)}
                        desc={lang === "mr" ? "दर महिन्याला नियमित उत्पन्न मिळवा." : "Earn guaranteed monthly income on lumpsum."}
                        to="/deposits"
                        testid={TID.serviceCard("mip")}
                        accent="bg-red-50 text-red-700"
                    />
                    <ServiceCard
                        icon="fa-hand-holding-dollar"
                        title={tr(t.loans.title)}
                        desc={lang === "mr" ? "गृह, व्यवसाय, सोने, वाहन व शिक्षण कर्ज." : "Home, business, gold, vehicle & education loans."}
                        to="/loans"
                        testid={TID.serviceCard("loans")}
                        accent="bg-amber-50 text-amber-700"
                    />
                </div>
            </section>

            {/* WHY CHOOSE US */}
            <section className="bg-gradient-to-b from-slate-50 to-white border-y border-slate-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
                    <div className="text-center max-w-3xl mx-auto mb-12">
                        <div className="text-xs uppercase tracking-widest text-amber-600 font-bold mb-2">
                            {lang === "mr" ? "विश्वासाची कारणे" : "The Sukhakarta Advantage"}
                        </div>
                        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-3">
                            {tr(t.home.whyTitle)}
                        </h2>
                        <p className="text-slate-600 text-base sm:text-lg">{tr(t.home.whySub)}</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {t.whyChoose.map((w, i) => (
                            <div
                                key={i}
                                className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-md hover:border-amber-500/40 transition-all"
                            >
                                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-red-700 to-amber-600 text-white flex items-center justify-center mb-4">
                                    <i className={`fa-solid ${w.icon} text-xl`} />
                                </div>
                                <h3 className="text-lg font-bold text-slate-900 mb-1.5">{tr(w.title)}</h3>
                                <p className="text-sm text-slate-600 leading-relaxed">{tr(w.desc)}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* TESTIMONIALS */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
                <div className="text-center max-w-3xl mx-auto mb-12">
                    <div className="text-xs uppercase tracking-widest text-amber-600 font-bold mb-2">
                        {lang === "mr" ? "सभासदांचे अनुभव" : "Testimonials"}
                    </div>
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900">
                        {tr(t.home.testimonialsTitle)}
                    </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    {t.testimonials.map((tm, i) => (
                        <div
                            key={i}
                            data-testid={TID.testimonialCard(i)}
                            className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all"
                        >
                            <div className="text-amber-500 mb-3">
                                {[0, 1, 2, 3, 4].map((s) => (
                                    <i key={s} className="fa-solid fa-star text-sm mr-0.5" />
                                ))}
                            </div>
                            <p className="text-slate-700 text-[15px] leading-relaxed italic mb-5">
                                "{tr(tm.quote)}"
                            </p>
                            <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-700 to-amber-600 text-white flex items-center justify-center font-bold">
                                    {tr(tm.name).split(" ").pop()[0]}
                                </div>
                                <div>
                                    <div className="font-semibold text-slate-900 text-sm">{tr(tm.name)}</div>
                                    <div className="text-xs text-slate-500">{tr(tm.role)}</div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* CTA Band */}
            <section className="bg-gradient-to-r from-red-700 via-red-700 to-amber-600 text-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 flex flex-col md:flex-row items-center justify-between gap-6">
                    <div>
                        <h3 className="text-2xl sm:text-3xl font-bold mb-1.5">
                            {lang === "mr" ? "आजच आमचे सभासद व्हा!" : "Become a Sukhakarta Member Today!"}
                        </h3>
                        <p className="text-amber-50 text-sm sm:text-base">
                            {lang === "mr"
                                ? "साधी प्रक्रिया, त्वरित मंजुरी आणि आजीवन विश्वास."
                                : "Simple process, quick approval, and a lifetime of trust."}
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-3">
                        <Link
                            to="/membership"
                            className="bg-white text-red-700 hover:bg-amber-50 font-bold px-6 py-3.5 rounded-lg transition-colors"
                        >
                            {tr(t.cta.joinMember)}
                        </Link>
                        <Link
                            to="/calculator"
                            className="bg-slate-900/30 hover:bg-slate-900/50 backdrop-blur-sm border border-white/30 font-semibold px-6 py-3.5 rounded-lg transition-colors"
                        >
                            {tr(t.cta.calculate)}
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Home;
